import { makeAutoObservable, reaction, runInAction } from 'mobx';

import {
  GLOBAL_HAS_NO_RECORDINGS,
  SITE_ID_STORAGE_KEY,
} from 'App/constants/storageKeys';
import { projectsService } from 'App/services';

import GDPR from './types/gdpr';
import Project from './types/project';

interface Config {
  project: Project | null;
  pid: number | undefined;
  tab: string;
}

export default class BaseProjectsStore {
  list: Project[] = [];
  instance: Project | null = null;
  siteId: string | null = null;
  tenantId: string | null = null;
  previousSiteid: string | null = null;
  active: Project | null = null;
  sitesLoading = true;
  loading = false;

  config: Config = {
    project: null,
    pid: undefined,
    tab: 'installation',
  };

  constructor() {
    makeAutoObservable(this);

    reaction(
      () => this.activeSiteId,
      (_, prevId) => {
        if (prevId) {
          this.previousSiteid = prevId;
        }
      },
    );
  }

  get isMobile() {
    return this.active
      ? ['ios', 'android'].includes(this.active.platform)
      : false;
  }

  get activeSiteId() {
    return this.active?.id || this.siteId;
  }

  syncProjectInList = (project: Partial<Project>) => {
    const index = this.list.findIndex((site) => site.id === project.id);
    if (index !== -1) {
      this.listWrites += 1;
      this.list[index] = this.list[index].edit(project);
      this.relink();
    }
  };

  getSiteId = () => ({
    siteId: this.siteId,
    active: this.active,
  });

  initProject = (project: Partial<Project>) => {
    this.instance = new Project(project);
  };

  setSitesLoading = (loading: boolean) => {
    this.sitesLoading = loading;
  };

  setLoading = (loading: boolean) => {
    this.loading = loading;
  };

  setSiteId = (siteId: string) => {
    if (this.tenantId) {
      localStorage.setItem(SITE_ID_STORAGE_KEY, `${siteId}_$_${this.tenantId}`);
    }
    this.siteId = siteId;
    this.active = this.list.find((site) => site.id! === siteId) ?? null;
  };

  setTenantId = (tenantId: string) => {
    this.tenantId = tenantId;
  };

  editGDPR = (gdprData: Partial<GDPR>) => {
    if (this.instance) {
      const oldInstance = this.instance.gdpr;
      this.instance.gdpr = new GDPR({
        ...oldInstance.toData(),
        ...gdprData,
      });
    }
  };

  editInstance = (instance: Partial<Project>) => {
    if (!this.instance) return;
    this.instance = this.instance.edit(instance);
  };

  fetchGDPR = async (siteId: string) => {
    try {
      const response = await projectsService.fetchGDPR(siteId);
      runInAction(() => {
        if (this.instance) {
          Object.assign(this.instance.gdpr, response.data);
        }
      });
    } catch (error) {
      console.error('Failed to fetch GDPR:', error);
    }
  };

  saveGDPR = async (siteId: string) => {
    if (!this.instance) return;
    try {
      const gdprData = this.instance.gdpr.toData();
      const response = await projectsService.saveGDPR(siteId, gdprData);
      this.editGDPR(response.data);

      try {
        this.syncProjectInList({
          id: siteId,
          gdpr: response.data,
        });
      } catch (error) {
        console.error('Failed to sync project in list:', error);
      }
    } catch (error) {
      console.error('Failed to save GDPR:', error);
    }
  };

  checkStoredId = (tenantId?: string) => {
    const existing = localStorage.getItem(SITE_ID_STORAGE_KEY);
    if (existing) {
      const [storedSiteId, storedTenantId] = existing.split('_$_');
      if (tenantId && String(tenantId) === storedTenantId) {
        return storedSiteId;
      }
    }
  };

  /** Pass the returned promise to `fetchList` as `pending`. */
  prefetchList = () => {
    this.setSitesLoading(true);
    const pending = projectsService.fetchList();
    // fetchList awaits this inside its own try/catch; this keeps an early
    // rejection from surfacing as unhandled in the meantime.
    pending.catch(() => {});
    return pending;
  };

  fetchList = async (
    siteIdFromPath?: string,
    tenantId?: string,
    pending?: ReturnType<typeof projectsService.fetchList>,
  ) => {
    this.setSitesLoading(true);
    try {
      const response = await (pending ?? projectsService.fetchList());
      runInAction(() => {
        this.list = response.data.map((data) => new Project(data));
        const siteIds = this.list.map((site) => site.id);
        let { siteId } = this;
        const siteExists = siteId ? siteIds.includes(siteId) : false;
        const lastStoredId = this.checkStoredId(tenantId);

        if (siteIdFromPath && siteIds.includes(siteIdFromPath)) {
          siteId = siteIdFromPath;
        } else if (lastStoredId && siteIds.includes(lastStoredId)) {
          siteId = lastStoredId;
        } else if (!siteId || !siteExists) {
          siteId = siteIds.includes(this.siteId)
            ? this.siteId
            : response.data[0].projectId;
        }

        const hasRecordings = this.list.some((site) => site.recorded);
        if (!hasRecordings) {
          localStorage.setItem(GLOBAL_HAS_NO_RECORDINGS, 'true');
        } else {
          localStorage.removeItem(GLOBAL_HAS_NO_RECORDINGS);
        }
        if (siteId) {
          this.setSiteId(siteId);
        }
      });
    } catch (error) {
      console.error('Failed to fetch site list:', error);
    } finally {
      this.setSitesLoading(false);
    }
  };

  // bumped by every local write, so a refresh that started earlier can't undo it
  private listWrites = 0;

  /** `active` and `config.project` are references into `list`: re-point them
      after the list or one of its items is replaced. */
  private relink = () => {
    if (this.siteId)
      this.active = this.list.find((site) => site.id === this.siteId) ?? null;
    if (this.config.pid != null) {
      const project = this.list.find(
        (site) => site.projectId === this.config.pid,
      );
      if (project) this.config.project = project;
    }
    if (this.list.some((site) => site.recorded)) {
      localStorage.removeItem(GLOBAL_HAS_NO_RECORDINGS);
    } else {
      localStorage.setItem(GLOBAL_HAS_NO_RECORDINGS, 'true');
    }
  };

  /** Re-reads the list in place. No loading flag: the app shell renders a
      spinner instead of the routes while `sitesLoading` is set. */
  refreshList = async () => {
    const writes = this.listWrites;
    try {
      const response = await projectsService.fetchList();
      if (writes !== this.listWrites) return;
      runInAction(() => {
        this.list = response.data.map((data) => new Project(data));
        this.relink();
      });
    } catch (error) {
      console.error('Failed to refresh site list:', error);
    }
  };

  save = async (projectData: Partial<Project>) => {
    this.setLoading(true);
    try {
      const response = await projectsService.saveProject(projectData);

      const newSite = new Project(response.data);
      runInAction(() => {
        this.listWrites += 1;
        const index = this.list.findIndex((site) => site.id === newSite.id);
        if (index !== -1) {
          this.list[index] = newSite;
        } else {
          this.list.push(newSite);
        }
        this.relink();
      });
      // this.setSiteId(newSite.id!);
      // this.active = newSite;
      return newSite;
    } catch (error: any) {
      throw error || 'An error occurred while saving the project.';
    } finally {
      this.setLoading(false);
    }
  };

  updateProjectRecordingStatus = (siteId: string, status: boolean) => {
    const site = this.list.find((site) => site.id === siteId);
    if (site) {
      this.listWrites += 1;
      site.recorded = status;
      this.relink();
    }
  };

  removeProject = async (projectId: string) => {
    this.setLoading(true);
    try {
      await projectsService.removeProject(projectId);
      runInAction(() => {
        this.listWrites += 1;
        this.list = this.list.filter((site) => site.id !== projectId);
        this.setConfigProject();
        if (this.active?.id === projectId) {
          this.setSiteId(this.list[0].id!);
        }
        this.relink();
      });
    } catch (error) {
      throw error || new Error('An error occurred while deleting the project.');
    } finally {
      this.setLoading(false);
    }
  };

  updateProject = async (projectId: string, projectData: Partial<Project>) => {
    this.setLoading(true);
    try {
      const response = await projectsService.updateProject(projectId, {
        name: projectData.name,
        platform: projectData.platform,
      });
      runInAction(() => {
        const updatedSite = new Project(response.data);
        this.listWrites += 1;
        const index = this.list.findIndex((site) => site.id === updatedSite.id);
        if (index !== -1) {
          this.list[index] = updatedSite;
        }
        this.relink();
      });
    } catch (error) {
      throw error || new Error('An error occurred while updating the project.');
    } finally {
      this.setLoading(false);
    }
  };

  setConfigProject = (pid?: number) => {
    if (!pid) {
      // the project being worked in, else the first one
      const fallback =
        this.list.find((p) => p.id === this.active?.id) ?? this.list[0];
      this.config.pid = fallback?.projectId ?? undefined;
      this.config.project = fallback ?? null;
      return;
    }

    const project = this.list.find((site) => site.projectId === pid);
    if (!project) {
      // set the first project as active
      this.setConfigProject();
    } else {
      const newConfig = {
        pid: project.projectId,
        project: project,
        tab: this.config.tab,
      };
      this.config = newConfig;
    }
  };

  setConfigTab = (tab: string | null) => {
    this.config.tab = tab ?? 'installation';
  };
}
